"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.isArrayOfNumbers = exports.isNumber = exports.roundDown = exports.roundUp = exports.roundUpOrDown = exports.zeroPad = exports.Calculation = exports.NumberRange = exports.NumberComparison = exports.shortPriceFormat = exports.longPriceFormat = exports.shortNumberFormat = exports.numericFormat = exports.abbreviate = exports.roundToDecimals = void 0;
function roundToDecimals(num = 0, decimals = 2) {
    const roundTo = Math.pow(10, decimals);
    return Math.round(num * roundTo) / roundTo;
}
exports.roundToDecimals = roundToDecimals;
const abbreviate = (numBytes = 0, fractionDigits = 3) => {
    let bytes = numBytes;
    const base = 1000;
    const prefix = ["", "k", "M", "G"];
    let unit = 0;
    while (Math.abs(bytes) >= base && unit < prefix.length - 1) {
        bytes /= base;
        unit += 1;
    }
    const number = bytes.toFixed(fractionDigits).replace(/[.0]*$/, "");
    return `${number}${prefix[unit]}`;
};
exports.abbreviate = abbreviate;
const numericFormat = (number, style, currency, currencyDisplay, notation, compactDisplay, minimumFractionDigits = 0, maximumFractionDigits = 2) => {
    const numericFormatOptions = {
        style,
        currency,
        currencyDisplay,
        notation,
        compactDisplay,
        minimumFractionDigits,
        maximumFractionDigits
    };
    return new Intl.NumberFormat("en-US", numericFormatOptions).format(number);
};
exports.numericFormat = numericFormat;
const shortNumberFormat = (number) => exports.numericFormat(number, undefined, undefined, "code", "compact", "short");
exports.shortNumberFormat = shortNumberFormat;
const longPriceFormat = (number, currency) => exports.numericFormat(number, "currency", currency, "code", "standard", undefined, 2);
exports.longPriceFormat = longPriceFormat;
const shortPriceFormat = (number, currency, showDecimals = false) => exports.numericFormat(number, "currency", currency, "code", "compact", "short", showDecimals ? 2 : 0);
exports.shortPriceFormat = shortPriceFormat;
class NumberComparison {
    constructor(value, type) {
        this.value = value;
        this.type = type;
    }
    match(value) {
        if (this.type === ">") {
            return value > this.value;
        }
        if (this.type === ">=") {
            return value >= this.value;
        }
        if (this.type === "<") {
            return value < this.value;
        }
        if (this.type === "<=") {
            return value <= this.value;
        }
        if (this.type === "=") {
            return value === this.value;
        }
        return false;
    }
    static fromObj(data, defaultType = "=") {
        var _a, _b;
        const value = Number((_a = data.value) !== null && _a !== void 0 ? _a : data);
        const type = (_b = data.type) !== null && _b !== void 0 ? _b : defaultType;
        return new NumberComparison(value, type);
    }
}
exports.NumberComparison = NumberComparison;
class NumberRange {
    constructor(minimum, maximum) {
        this.minimum = minimum;
        this.maximum = maximum;
        if (minimum && minimum.type !== ">" && minimum.type !== ">=") {
            throw new Error("invalid range minimum");
        }
        if (maximum && maximum.type !== "<" && maximum.type !== "<=") {
            throw new Error("invalid range maximum");
        }
        if (minimum &&
            maximum &&
            (minimum === null || minimum === void 0 ? void 0 : minimum.value) === (maximum === null || maximum === void 0 ? void 0 : maximum.value) &&
            ((minimum === null || minimum === void 0 ? void 0 : minimum.type) !== ">=" || (maximum === null || maximum === void 0 ? void 0 : maximum.type) !== "<=")) {
            throw new Error("invalid range");
        }
    }
    static fromObj(data) {
        const minimum = NumberComparison.fromObj(data.minimum, ">=");
        const maximum = NumberComparison.fromObj(data.maximum, "<=");
        return new NumberRange(minimum, maximum);
    }
    isInRange(value) {
        return ((!this.minimum || this.minimum.match(value)) &&
            (!this.maximum || this.maximum.match(value)));
    }
    toString() {
        const min = this.minimum
            ? `${this.minimum.type === ">" ? "(" : "["}${this.minimum.value}`
            : "";
        const max = this.maximum
            ? `${this.maximum.value}${this.maximum.type === "<" ? ")" : "]"}`
            : "";
        return `${min}-${max}`;
    }
    union(unRange) {
        let { minimum: min, maximum: max } = this;
        const { minimum: uMin, maximum: uMax } = unRange;
        if (!uMin) {
            min = uMin;
        }
        else if (min) {
            if (min.value > uMin.value) {
                min = uMin;
            }
            else if (min.value === uMin.value && uMin.type === ">=") {
                min = uMin;
            }
        }
        if (!uMax) {
            max = uMax;
        }
        else if (max) {
            if (max.value < uMax.value) {
                max = uMax;
            }
            else if (max.value === uMax.value && uMax.type === "<=") {
                max = uMax;
            }
        }
        return new NumberRange(min, max);
    }
    static unionRanges(ranges) {
        return ranges.reduce((prev, curr) => (!prev ? curr : prev.union(curr)));
    }
    intersect(nRange) {
        let { minimum: min, maximum: max } = this;
        const { minimum: uMin, maximum: uMax } = nRange;
        if (uMin) {
            if (!min) {
                min = uMin;
            }
            else if (min.value < uMin.value) {
                min = uMin;
            }
            else if (min.value === uMin.value && uMin.type === ">") {
                min = uMin;
            }
        }
        if (uMax) {
            if (!max) {
                max = uMax;
            }
            else if (max.value > uMax.value) {
                max = uMax;
            }
            else if (max.value === uMax.value && uMax.type === "<") {
                max = uMax;
            }
        }
        return new NumberRange(min, max);
    }
    static intersectRanges(ranges) {
        return ranges.reduce((prev, curr) => (!prev ? curr : prev.intersect(curr)));
    }
}
exports.NumberRange = NumberRange;
class Calculation {
    constructor(value, type, minimum) {
        this.value = value;
        this.type = type;
        this.minimum = minimum;
    }
    calculate(value, minValue = 0) {
        var _a, _b;
        if (this.type === "=") {
            return Math.max(this.value, (_a = this.minimum) !== null && _a !== void 0 ? _a : minValue);
        }
        if (this.type === "%") {
            return Math.max((value * this.value) / 100, (_b = this.minimum) !== null && _b !== void 0 ? _b : minValue);
        }
        throw new Error(`Invalid calculate data value=${this.value} type=${this.type}`);
    }
    static fromObj(data) {
        return new Calculation(data.value, data.type, data.minimum);
    }
}
exports.Calculation = Calculation;
function zeroPad(value, length) {
    const num = typeof value === "number" ? value : Number(value);
    return String(num).padStart(length, "0");
}
exports.zeroPad = zeroPad;
const roundUpOrDown = (num, round, breakPosition) => {
    if (num === 0) {
        return 0;
    }
    const size = Math.floor(Math.log10(num)) + 1;
    const shiftFactor = Math.pow(10, (size - breakPosition + 1));
    const significantNum = (num * 2) / shiftFactor;
    const roundFunc = round === "up" ? Math.ceil : Math.floor;
    const roundedNum = roundFunc(significantNum);
    const num2 = roundedNum / 2;
    const final = roundToDecimals(num2 * shiftFactor);
    return final;
};
exports.roundUpOrDown = roundUpOrDown;
const roundUp = (num, breakPosition = 5) => {
    if (num < 0) {
        return -exports.roundUpOrDown(-num, "down", breakPosition);
    }
    return exports.roundUpOrDown(num, "up", breakPosition);
};
exports.roundUp = roundUp;
const roundDown = (num, breakPosition = 5) => {
    if (num < 0) {
        return -exports.roundUpOrDown(-num, "up", breakPosition);
    }
    return exports.roundUpOrDown(num, "down", breakPosition);
};
exports.roundDown = roundDown;
const isNumber = (element) => typeof element === "number";
exports.isNumber = isNumber;
const isArrayOfNumbers = (array) => array.every((element) => exports.isNumber(element));
exports.isArrayOfNumbers = isArrayOfNumbers;
//# sourceMappingURL=index.js.map